import { Component, DestroyRef, Inject, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, concatMap, map, startWith, tap } from 'rxjs/operators';
import {
  EnhancementVariantDetail,
  IMasterSuite,
  ISettingsVariant,
} from 'src/app/models/mastersuites/mastersuite';
import { IProduct } from 'src/app/models/products/product';
import { AlertService } from 'src/app/services/alert.service';
import { AuthService } from 'src/app/services/auth.service';
import { MasterSuitesService } from 'src/app/services/mastersuites.service';
import { ProductsService } from 'src/app/services/products.service';
import { Permission } from 'src/app/constants/permission.constant';

export interface ApplyMastersuiteDialogData {
  products: IProduct[];
  masterSuites: IMasterSuite[];
}

@Component({
  selector: 'app-mastersuite-apply',
  templateUrl: './mastersuite-apply.component.html',
  styleUrls: ['./mastersuite-apply.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class MastersuiteApplyComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  mastersuiteControl = new FormControl();
  mastersuiteFilteredOptions!: Observable<IMasterSuite[]>;

  selectedMastersuite: IMasterSuite | null = null;
  selectedVariant: ISettingsVariant | undefined;
  selectedEnhancementVariant: EnhancementVariantDetail | undefined;
  variants: ISettingsVariant[] = [];
  enhancementVariants: EnhancementVariantDetail[] = [];

  isApplying = false;

  Permission = Permission;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ApplyMastersuiteDialogData,
    private dialogRef: MatDialogRef<MastersuiteApplyComponent>,
    private masterSuitesService: MasterSuitesService,
    private productsService: ProductsService,
    private alertService: AlertService,
    public authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.mastersuiteFilteredOptions = this.mastersuiteControl.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterMastersuites(value)),
    );
  }

  displayFn(mastersuite: IMasterSuite): string {
    return mastersuite?.name ?? '';
  }

  onMastersuiteSelected(mastersuite: IMasterSuite): void {
    this.resetMastersuiteSelection(mastersuite);
    this.loadSettingsVariants(mastersuite.id!);
    this.loadEnhancementVariants(mastersuite.id!);
  }

  onApply(): void {
    if (!this.selectedMastersuite) return;

    this.isApplying = true;
    const productIds = this.getProductIds();

    this.applyMasterSuite$(productIds).pipe(
      tap(() => this.toastMasterSuiteApplied(productIds.length)),
      concatMap(() => this.applyDependentSteps$(productIds)),
      catchError(() => { this.finishApply(false); return EMPTY; }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.finishApply(true));
  }

  private resetMastersuiteSelection(mastersuite: IMasterSuite): void {
    this.selectedMastersuite = mastersuite;
    this.selectedVariant = undefined;
    this.selectedEnhancementVariant = undefined;
    this.variants = [];
    this.enhancementVariants = [];
  }

  private loadSettingsVariants(masterSuiteId: string): void {
    this.masterSuitesService.detailMasterSuite(masterSuiteId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (detail) => {
          this.variants = detail.settingsVariants ?? [];
          if (this.variants.length === 1) {
            this.selectedVariant = this.variants[0];
          }
        },
      });
  }

  private loadEnhancementVariants(masterSuiteId: string): void {
    this.masterSuitesService.getAllEnhancementVariantOfMasterSuite(masterSuiteId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.enhancementVariants = res?.enhancementVariants ?? [];
          if (this.enhancementVariants.length === 1) {
            this.selectedEnhancementVariant = this.enhancementVariants[0];
          }
        },
      });
  }

  private getProductIds(): string[] {
    return this.data.products.map((product) => product.id!);
  }

  private hasSettingsVariantSelection(): boolean {
    return !!this.selectedVariant;
  }

  private hasEnhancementVariantSelection(): boolean {
    return !!this.selectedEnhancementVariant;
  }

  private applyMasterSuite$(productIds: string[]): Observable<unknown> {
    return this.productsService.applyMasterSuiteOnProducts({
      masterSuiteId: this.selectedMastersuite!.id!,
      productIds,
      version: 0,
    });
  }

  private applyDependentSteps$(productIds: string[]): Observable<unknown> {
    return of(null).pipe(
      concatMap(() => this.applyOptionalSettingsVariant$(productIds)),
      concatMap(() => this.applyOptionalEnhancementVariant$(productIds)),
      catchError(() => { this.finishApply(true); return EMPTY; }),
    );
  }

  private applyOptionalSettingsVariant$(productIds: string[]): Observable<unknown> {
    if (!this.hasSettingsVariantSelection()) return of(null);
    return this.productsService.applySettingsVariantOnProducts({
      settingsVariantId: this.selectedVariant!.id!,
      productIds,
      version: 0,
    }).pipe(tap(() => this.toastSettingsVariantApplied()));
  }

  private applyOptionalEnhancementVariant$(productIds: string[]): Observable<unknown> {
    if (!this.hasEnhancementVariantSelection()) return of(null);
    return this.productsService.applyEnhancementVariantOnProducts({
      enhancementVariantId: this.selectedEnhancementVariant!.id,
      productIds,
      version: 0,
    }).pipe(tap(() => this.toastEnhancementVariantApplied()));
  }

  private toastMasterSuiteApplied(productCount: number): void {
    this.alertService.successToast(
      `Mastersuite ${this.selectedMastersuite!.name} applied to ${productCount} product(s)`,
    );
  }

  private toastSettingsVariantApplied(): void {
    this.alertService.successToast(`Settings Variant ${this.selectedVariant!.name} applied`);
  }

  private toastEnhancementVariantApplied(): void {
    this.alertService.successToast(
      `Enhancement Variant ${this.selectedEnhancementVariant!.name} applied`,
    );
  }

  private finishApply(shouldClose: boolean): void {
    this.isApplying = false;
    if (shouldClose) this.dialogRef.close(true);
  }

  private filterMastersuites(value: string | IMasterSuite): IMasterSuite[] {
    const filterValue =
      typeof value === 'string' ? value.toLowerCase() : value?.name?.toLowerCase() ?? '';
    return filterValue
      ? this.data.masterSuites.filter((mastersuite) =>
          mastersuite.name!.toLowerCase().includes(filterValue),
        )
      : [...this.data.masterSuites];
  }
}
